import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { resolveImg } from "@/components/admin/ProductsTab";
import { Tag, Clock, ArrowRight } from "lucide-react";
import ProductCard from "@/components/ProductCard";

async function fetchActiveCampaign() {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from("campaigns")
    .select("*")
    .eq("is_enabled", true)
    .lte("start_date", now)
    .gte("end_date", now)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
    
  if (error || !data) return null;
  
  if (data.product_ids && data.product_ids.length > 0) {
    const { data: prods } = await supabase
      .from("products")
      .select("*")
      .in("id", data.product_ids);
    data.products = prods || [];
  }
  return data;
}

export default function CampaignBanner() {
  const [camp, setCamp] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchActiveCampaign()
      .then(c => { if (mounted) setCamp(c); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!camp) return null;

  return (
    <section
      className="border-y-[3px] border-black relative overflow-hidden"
      style={{ background: camp.banner_color }}
      data-testid="campaign-banner"
    >
      {/* Animated dots overlay */}
      <div className="absolute inset-0 candy-dots opacity-30 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative">
        <div className="lg:col-span-7 text-white">
          <div className="inline-flex items-center gap-2 nb-pill !text-black mb-4">
            <Tag size={14} strokeWidth={3} /> CAMPAIGN LIVE NOW
          </div>
          <h2 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tight leading-[0.95] drop-shadow-[2px_2px_0_#0A0A0A]">
            {camp.title}
          </h2>
          {camp.subtitle && (
            <p className="mt-3 text-lg md:text-xl text-white/95 max-w-xl">{camp.subtitle}</p>
          )}
          <div className="mt-5 inline-block bg-white text-black border-[3px] border-black rounded-full px-5 py-2 font-display font-black uppercase text-2xl md:text-3xl shadow-[5px_5px_0_0_#0A0A0A] -rotate-1">
            {camp.discount_text}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Countdown endDate={camp.end_date} />
            <Link to="/shop" className="nb-btn bg-black text-white inline-flex items-center gap-2" data-testid="campaign-cta">
              {camp.cta_text || "Shop the Offer"} <ArrowRight strokeWidth={3} />
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5">
          {camp.banner_image ? (
            <div className="aspect-[4/3] rounded-[28px] nb-border overflow-hidden nb-shadow-lg">
              <img src={resolveImg(camp.banner_image)} alt={camp.title} className="w-full h-full object-cover" />
            </div>
          ) : camp.products?.length ? (
            <div className="grid grid-cols-2 gap-3">
              {camp.products.slice(0, 4).map((p) => (
                <div key={p.id} className="aspect-square rounded-[20px] border-[3px] border-black overflow-hidden bg-white">
                  <img src={resolveImg(p.images?.[0])} alt={p.name} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {/* Scrolling product strip */}
      {camp.products?.length > 0 && (
        <div className="bg-[var(--candy-purple)] border-t-[3px] border-black overflow-hidden">
          <div className="marquee-track py-5 gap-5">
            {[...camp.products, ...camp.products].map((p, i) => (
              <Link
                key={`${p.id}-${i}`}
                to={`/product/${p.id}`}
                className="shrink-0 w-56 nb-card !bg-white inline-block mx-3"
                data-testid={`campaign-product-${i}`}
              >
                <div className="aspect-square overflow-hidden border-b-[3px] border-black">
                  <img src={resolveImg(p.images?.[0])} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="font-display font-bold truncate">{p.name}</div>
                  <div className="font-display font-black text-[var(--primary)]">₹{p.price}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Countdown({ endDate }) {
  const target = useMemo(() => new Date(endDate).getTime(), [endDate]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff / 3600000) % 24);
  const mins = Math.floor((diff / 60000) % 60);
  const secs = Math.floor((diff / 1000) % 60);

  return (
    <div className="inline-flex items-center gap-3 nb-pill !text-black bg-white" data-testid="campaign-countdown">
      <Clock size={16} strokeWidth={3} />
      <span className="font-display font-black tabular-nums">
        {days}d {String(hours).padStart(2, "0")}h {String(mins).padStart(2, "0")}m {String(secs).padStart(2, "0")}s
      </span>
    </div>
  );
}

export function OfferStrip() {
  const [camp, setCamp] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchActiveCampaign()
      .then(c => { if (mounted) setCamp(c); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!camp) return null;

  const items = [
    `${camp.discount_text}`,
    camp.title,
    camp.subtitle || "LIMITED TIME ONLY",
    "BUY ON WHATSAPP",
    "FREE LOCAL DELIVERY",
  ];
  const loop = [...items, ...items, ...items];

  return (
    <div
      className="border-b-[3px] border-black overflow-hidden text-white"
      style={{ background: camp.banner_color }}
      data-testid="offer-strip"
    >
      <div className="marquee-track py-2">
        {loop.map((t, i) => (
          <span key={i} className="font-display font-black uppercase text-sm md:text-base tracking-wider px-6 inline-flex items-center gap-3">
            <Tag size={14} strokeWidth={3} />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

// Re-export for ProductCard usage
export { ProductCard };

